/**
 * src/components/composed/ViewerChip.tsx — "You are X" affordance.
 *
 * The minimum AppShell affordance for soft identity: every signed-in user
 * sees who they're acting as. Designed to slot into the global AppTopbar
 * (and ForecastMarket's bespoke head__actions where the topbar is
 * suppressed) alongside the other utility controls.
 *
 * Design-system aware (PR 1): renders as a button that matches the rest
 * of the topbar's controls via --ds-* variables. Clicking opens a small
 * popover menu with the actions previously inlined (View profile, Switch
 * user). The "watching as" state keeps its distinct warn-colored
 * treatment so an admin can't forget they're spoofing.
 *
 * Behavior:
 *   - Renders nothing if no viewer (e.g. before the picker has been used).
 *   - Click → opens popover with "Profile" + "Switch user".
 *   - Switch user → confirm → clear localStorage["forecast_active_user_id"]
 *     and reload. View password persists; identity resets, lands on
 *     picker without retyping.
 *
 * Spoof-resistance: switching is purely client-side. Per §1.6 of the
 * platform doc, the picker is soft identity by design — the audit log
 * (Phase 5.7) attributes every write to whichever id was active at
 * write time.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { useViewer } from "../../lib/data/viewer";
// Phase 4e — IT/EN parity. ViewerChip shows in every header (forecast,
// admin, profile) so its strings need to follow the global lang.
import { useLang } from "../../lib/lang";

const STORAGE_KEY = "forecast_active_user_id";

export interface ViewerChipProps {
  /** Compact form — name only, no label. For tight headers. Defaults to false. */
  compact?: boolean;
  /** Disable confirm dialog (used by tests / debug paths). Defaults to false. */
  skipConfirm?: boolean;
  /** Override style on the outer button — caller's responsibility to keep it readable. */
  style?: React.CSSProperties;
}

export default function ViewerChip({ compact = false, skipConfirm = false, style }: ViewerChipProps) {
  const { viewer, realViewer, isWatching, stopWatching } = useViewer();
  const lang = useLang();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const wrapRef = React.useRef<HTMLDivElement>(null);

  // Close popover on outside click + Escape.
  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!viewer) return null;

  const handleSwitch = () => {
    const ok = skipConfirm || confirm(
      lang === "it"
        ? "Cambia utente? Tornerai al picker. La password di visualizzazione resta salvata."
        : "Switch user? You'll go back to the picker. The view password stays saved."
    );
    if (!ok) return;
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
    location.reload();
  };

  const goToProfile = () => {
    setOpen(false);
    navigate("/me");
  };

  // Phase 6.2 — watching another user. Render in a distinct warn-colored
  // treatment so the admin doesn't forget. Keeps the original "Esci/Exit"
  // affordance inline because exiting is the primary action in this state.
  if (isWatching && realViewer) {
    const targetText = compact ? viewer.name : `${viewer.name} · ${viewer.label || viewer.level}`;
    return (
      <span
        className="viewer-chip viewer-chip--watching"
        style={style}
        title={lang === "it"
          ? `${realViewer.name} osserva come ${viewer.name} — sola lettura`
          : `${realViewer.name} watching as ${viewer.name} — read only`}
      >
        <span className="viewer-chip__watching-icon" aria-hidden="true">👁</span>
        <span className="viewer-chip__watching-text">
          {lang === "it" ? "Osservando" : "Watching"}{" "}
          <b>{targetText}</b>
        </span>
        <button
          type="button"
          onClick={() => stopWatching()}
          className="viewer-chip__exit"
        >
          {lang === "it" ? "Esci" : "Exit"}
        </button>
      </span>
    );
  }

  const labelText = compact
    ? viewer.name
    : `${viewer.name}${viewer.label ? " · " + viewer.label : ""}`;

  return (
    <div ref={wrapRef} className="viewer-chip-wrap" style={style}>
      <button
        type="button"
        className={`viewer-chip ${open ? "viewer-chip--open" : ""}`}
        onClick={() => setOpen(o => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={lang === "it"
          ? `Connesso come ${viewer.name}${viewer.isAdmin ? " (admin)" : ""}`
          : `Signed in as ${viewer.name}${viewer.isAdmin ? " (admin)" : ""}`}
      >
        <span className="viewer-chip__name">{labelText}</span>
        {viewer.isAdmin && (
          <span className="viewer-chip__admin-badge" aria-label="admin">
            admin
          </span>
        )}
        <svg className="viewer-chip__caret" width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
          <path d="M2 4 L5 7 L8 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className="viewer-chip__menu" role="menu">
          <button
            type="button"
            className="viewer-chip__menu-item"
            onClick={goToProfile}
            role="menuitem"
          >
            {lang === "it" ? "Profilo" : "Profile"}
          </button>
          <button
            type="button"
            className="viewer-chip__menu-item"
            onClick={() => { setOpen(false); handleSwitch(); }}
            role="menuitem"
          >
            {lang === "it" ? "Cambia utente" : "Switch user"}
          </button>
        </div>
      )}
    </div>
  );
}
